import { Button, DatePicker, TextField } from '@shared/ui/kit';
import { FC } from 'react';
import stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { CalendarIcon, ChevronLeftIcon } from '@shared/assets/icons';
import { InvitationType, StepInfo } from '@features/modal-invitation/model/types';
import { colors } from '@shared/tokens/colors.stylex';
import { formatDate } from '@shared/libs/format-date';
import { LockIcon } from '@shared/assets/icons/lock';
import { TicketDeliveryMethod } from '@pages/event/api/useSendMessages';
import { CustomDropdown } from '@shared/ui/dropdown';
import { Tiptap } from '@shared/ui/tiptap';
import { Alert, ALERT_VARIANTS } from '@features/modal-invitation/ui/alert';
import { StylesText } from '@entities/leadCollector';
import { UnstyledButton } from '@shared/ui/unstyled-button';

// Устаревший флоу отправки приглашений — шаг и связанный код будут удалены после перехода на новый флоу
// Пропсы не группируем так как компонент будет сносится.

const INVITATION_EXPIRES_ERROR_TEXT = 'Срок действия приглашения позже даты проведения мероприятия';

interface StepInvitationProps {
  selectedType: InvitationType | null;
  selectedTypeError: string | false;
  message: string;
  messageErrors: string | false;
  messageParts: number;
  handleDropdownChange: (value: InvitationType[] | InvitationType[keyof InvitationType][]) => void;
  changeHandler: (event: string) => void;
  handleNext: () => void;
  handleClose: () => void;
  stepInfo: StepInfo;
  invitationTypes: InvitationType[];
  invitationExpiresAt: Date | null;
  invitationExpiresAtError: string | false;
  eventDateEnd?: string;
  previousInvitationExpiresAt: string | null;
  isInvitationSettingsLocked: boolean;
  onInvitationDateChange: (date: Date | null) => void;
  onOpenSettings?: () => void;
  inviteAcceptionInfo: string;
  onChangeInviteAcceptionInfo: (value: string) => void;
}

export const StepInvitation: FC<StepInvitationProps> = ({
  selectedType,
  selectedTypeError,
  message,
  messageErrors,
  messageParts,
  handleDropdownChange,
  changeHandler,
  handleNext,
  handleClose,
  stepInfo,
  invitationTypes,
  invitationExpiresAt,
  invitationExpiresAtError,
  eventDateEnd,
  isInvitationSettingsLocked,
  onInvitationDateChange,
  onOpenSettings,
  inviteAcceptionInfo,
  onChangeInviteAcceptionInfo,
}) => {
  const shouldShowInlineError = invitationExpiresAtError && invitationExpiresAtError !== INVITATION_EXPIRES_ERROR_TEXT;
  const shouldShowWarningError = invitationExpiresAtError === INVITATION_EXPIRES_ERROR_TEXT;

  const isSmsMode = selectedType?.value === TicketDeliveryMethod.SMS;
  const isBothMode = selectedType?.value === TicketDeliveryMethod.Both;

  const handleOpenSettings = () => {
    handleClose();
    onOpenSettings?.();
  };

  const getTextarea = () => {
    if (isSmsMode || !selectedType?.value) {
      return (
        <>
          <p {...stylex.props(styles.textCount)}>
            {message.length} ({messageParts} смс)
          </p>

          <div {...stylex.props(styles.textFieldContainer)}>
            <div {...stylex.props(styles.labelPreviewText)}>Текст приглашения *</div>
            {messageErrors && <div {...stylex.props(styles.textareaErrorText)}>{messageErrors}</div>}
            <TextField
              labelMsg=""
              changeFunction={(event) => changeHandler(event.target.value.replace(/\r?\n/g, ''))}
              placeholder="Ввести текст"
              value={message}
              settings={{
                width: '432px',
                cursor: 'text',
                resize: 'none',
                height: '78px',
                minHeight: '64px',
                overflow: 'auto',
              }}
              status={messageErrors ? 'error' : 'default'}
              textareaMessage={messageErrors || undefined}
            />
          </div>
        </>
      );
    }

    return (
      <>
        <div {...stylex.props(styles.message)}>
          <Tiptap
            {...stylex.props(styles.tiptap)}
            placeholder="Текст приглашения"
            onChange={(html) => changeHandler(html)}
            properties={{ link: true, styles: [StylesText.BOLD, StylesText.ITALIC, StylesText.UNDERLINE] }}
            withToolbar
            hiddenStyles={[StylesText.LEFT, StylesText.CENTER, StylesText.RIGHT, StylesText.JUSTIFY]}
            content={message}
          />
        </div>

        {isBothMode && (
          <div {...stylex.props(styles.alertTopSpacing)}>
            <Alert variant={ALERT_VARIANTS.INFO} maxWidth="432px">
              Форматирование будет применено только к email
            </Alert>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <div {...stylex.props(styles.inputWrapper)}>
        <div {...stylex.props(styles.dropdownContainer)}>
          <div {...stylex.props(isInvitationSettingsLocked ? styles.dateWrapperLocked : styles.dateWrapper)}>
            <div {...stylex.props(styles.labelContainer)}>
              <p {...stylex.props(isInvitationSettingsLocked ? styles.labelTypeLocked : styles.labelType)}>
                Срок действия приглашения *
              </p>
              {isInvitationSettingsLocked && (
                <div {...stylex.props(styles.labelIcon)}>
                  {' '}
                  <LockIcon />
                </div>
              )}
            </div>
            {isInvitationSettingsLocked ? (
              <div {...stylex.props(styles.lockedDateContainer)}>
                <div {...stylex.props(styles.lockedDateText)}>
                  {invitationExpiresAt ? formatDate(invitationExpiresAt.toISOString()) : '-'}
                  <div {...stylex.props(styles.calendarIcon)}>
                    <CalendarIcon />
                  </div>
                </div>
              </div>
            ) : (
              <DatePicker
                labelText=""
                size="small"
                isRange={false}
                selectedDate={invitationExpiresAt}
                setNewSelectedDate={(dates) => onInvitationDateChange(dates[0] || null)}
              />
            )}
            {shouldShowInlineError && <div {...stylex.props(styles.errorText)}>{invitationExpiresAtError}</div>}
          </div>
          <div>
            <CustomDropdown
              labelMessage="Тип отправки приглашения *"
              placeholder="Выбрать тип"
              options={invitationTypes}
              itemText="label"
              itemValue="value"
              value={selectedType || undefined}
              isRadio={false}
              fullWidth
              changeFunc={(option) => {
                option && handleDropdownChange([option]);
              }}
            />
            {selectedTypeError && <div {...stylex.props(styles.errorText)}>{selectedTypeError}</div>}
          </div>
        </div>

        {getTextarea()}

        <div {...stylex.props(styles.previewContainer)}>
          <div {...stylex.props(styles.labelPreview)}>Предварительный просмотр приглашения</div>
          <div {...stylex.props(styles.areaPreview)}>
            {isSmsMode ? (
              <div {...stylex.props(styles.messagePreview)}>{message}</div>
            ) : (
              <div {...stylex.props(styles.tiptap, styles.previewText)}>
                <div dangerouslySetInnerHTML={{ __html: message }} />
              </div>
            )}
            <div {...stylex.props(styles.linkPreview)}>{globalThis.window.location.href}</div>
          </div>
        </div>

        <div
          {...stylex.props(styles.inviteAcceptionInfo, isInvitationSettingsLocked && styles.repeatInviteAcceptionInfo)}
        >
          <TextField
            labelMsg="Текст после принятия приглашения"
            changeFunction={(e) => onChangeInviteAcceptionInfo(e.target.value.replace(/\r?\n/g, ''))}
            placeholder="Введите текст"
            value={inviteAcceptionInfo}
            isDisabled={isInvitationSettingsLocked}
            settings={{
              width: '100%',
              cursor: 'text',
              resize: 'vertical',
              height: '78px',
              minHeight: '78px',
            }}
          />
        </div>

        {shouldShowWarningError && (
          <div {...stylex.props(styles.alertTopSpacing)}>
            <Alert variant={ALERT_VARIANTS.ERROR} maxWidth="432px">
              {INVITATION_EXPIRES_ERROR_TEXT}
              {eventDateEnd && ` ${formatDate(eventDateEnd)}`}
            </Alert>
          </div>
        )}
        {isInvitationSettingsLocked && (
          <div {...stylex.props(styles.alertTopSpacing)}>
            <Alert variant={ALERT_VARIANTS.INFO} maxWidth="432px">
              Срок действия приглашения задан в настройках мероприятия.{' '}
              <UnstyledButton {...stylex.props(styles.infoLink)} onClick={handleOpenSettings}>
                Перейти в настройки для изменения
              </UnstyledButton>
            </Alert>
          </div>
        )}
      </div>
      <div {...stylex.props(styles.actions)}>
        <div {...stylex.props(styles.stepsContainer)}>
          <Button size="small" variant="secondary" isDisabled>
            <ChevronLeftIcon color={colors.ghostIcon} />
          </Button>
          <div {...stylex.props(styles.stepInfo)}>
            <div {...stylex.props(styles.step)}>
              Шаг {stepInfo.current} из {stepInfo.total}
            </div>
            <div {...stylex.props(styles.info)}>
              Следующий: <span {...stylex.props(styles.nextStep)}>{stepInfo.next}</span>
            </div>
          </div>
        </div>
        <Button onClick={handleClose} variant="secondary" settings={{ padding: '8px 20px' }} size="small">
          Отмена
        </Button>

        <Button
          onClick={handleNext}
          isDisabled={
            message.length < 1 ||
            !selectedType ||
            (!invitationExpiresAt && !isInvitationSettingsLocked) ||
            !!invitationExpiresAtError
          }
          variant="accent"
          settings={{ padding: '8px 20px' }}
          size="small"
        >
          Далее
        </Button>
      </div>
    </>
  );
};
